
import Async from './Async';
import Promise from './Promise';
import Signal from './Signal';

export interface IRetryConfig<T> {
    /** Max number of retries */
    retries: number;

    /** Callback to invoke */
    callback: () => Promise<T>;

    /** Return true to call the callback again. */
    canRetry?: (error: any) => boolean;

    /**
     * How long to wait before calling the callback again.
     * If not provided, calls again immediately.
     * If provided, must also provide an Async instance.
     */
    delay?: number;

    /** Only required if `delay` is specified */
    async?: Async;

    /** If true and beforeComplete returns true, then reset the current retry count */
    resetAttemptsOnRestart?: boolean;

    /** Called before complete to determine if callback should be called again */
    shouldRestart?: () => boolean;

    /** Called before trying again, so you can adjust parameters */
    beforeRetry?: () => void;

    /** Callback to inspect the error and change the number of retries if necessary */
    adjustRetries?: (error: any) => number;
}

/**
 * Retry allows you to invoke a callback a configurable number of times before giving up.
 */
export default function Retry<T>(config: IRetryConfig<T>): Promise<T> {
    let {
        adjustRetries,
        async,
        beforeRetry,
        callback,
        canRetry = () => true,
        delay,
        resetAttemptsOnRestart,
        retries,
        shouldRestart
    } = config;

    let attempts = 0;
    let promise: Promise<T>;
    let signal = new Signal<T>(() => {
        promise.cancel();
    });

    function onSuccess(value: T) {
        if (shouldRestart && shouldRestart()) {
            if (resetAttemptsOnRestart) {
                attempts = 0;
            }
            callCallback();
        } else {
            signal.complete(value);
        }
    }

    function onError(error: any) {
        if (adjustRetries) {
            retries = adjustRetries(error);
        }
        if (attempts < retries && canRetry(error)) {
            attempts++;
            if (beforeRetry) {
                beforeRetry();
            }
            if (typeof delay === 'number' && async) {
                async.setTimeout(callCallback, delay);
            } else {
                callCallback();
            }
        } else {
            signal.error(error);
        }
    }

    function callCallback() {
        promise = callback();
        promise.then(onSuccess, onError);
    }

    callCallback();

    return signal.getPromise();
}
